import { useState, useEffect, useCallback } from "react";
import { Layout } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import jsPDF from "jspdf";
import {
  Gavel, Plus, Calendar, Clock, TrendingUp, Users, Eye,
  CheckCircle, XCircle, AlertCircle, Trash2, Edit, Timer,
  Award, Package, RefreshCw, ChevronDown, ChevronUp, Phone, Mail,
  Play, UserPlus, Shield, Euro, Printer, Camera
} from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_509254e3-4fbd-4a68-8194-b9dcfb6ff3e4/artifacts/bylh1er6_Image.jpg";

async function generateBadgePDF(client) {
  try {
    // Fetch badge data from backend (includes QR code)
    const res = await axios.get(`${API}/auction-clients/${client.client_id}/badge`);
    const badge = res.data;
    
    // Badge = credit card size: 85.6 x 54 mm
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: [54, 85.6] });
    const w = 85.6, h = 54;

    // Background
    doc.setFillColor(18, 18, 18);
    doc.rect(0, 0, w, h, "F");

    // Top green stripe
    doc.setFillColor(50, 180, 80);
    doc.rect(0, 0, w, 8, "F");

    // Logo + SCUTERSH text in stripe
    try {
      const logoResp = await fetch(LOGO_URL);
      const logoBlob = await logoResp.blob();
      const logoB64 = await new Promise(r => { const rd = new FileReader(); rd.onloadend = () => r(rd.result); rd.readAsDataURL(logoBlob); });
      doc.addImage(logoB64, "JPEG", 2, 1, 6, 6);
    } catch (e) { console.warn("Badge logo error:", e); }
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("SCUTERSH", 10, 5.5);
    doc.setFontSize(5);
    doc.text("ECUSON DE ACCES", w - 3, 5.5, { align: "right" });

    // Client photo (left side)
    if (badge.photo_b64) {
      try {
        doc.addImage(`data:image/jpeg;base64,${badge.photo_b64}`, "JPEG", 3, 11, 20, 20);
      } catch (e) { console.warn("Badge photo error:", e); }
    } else {
      doc.setFillColor(40, 40, 40);
      doc.roundedRect(3, 11, 20, 20, 2, 2, "F");
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(6);
      doc.text("FARA", 13, 19, { align: "center" });
      doc.text("FOTO", 13, 23, { align: "center" });
    }

    // Client info (right of photo)
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(badge.name.toUpperCase(), 26, 15, { maxWidth: 38 });

    doc.setTextColor(180, 180, 180);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    doc.text(badge.city || "", 26, 21);
    doc.text(badge.phone || "", 26, 25);

    // Client ID badge
    doc.setFillColor(50, 180, 80);
    doc.roundedRect(26, 27, 22, 5, 1.5, 1.5, "F");
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text(badge.client_id, 37, 30.5, { align: "center" });

    // QR code (bottom right)
    if (badge.qr_code_b64) {
      try {
        doc.addImage(`data:image/png;base64,${badge.qr_code_b64}`, "PNG", w - 18, 11, 15, 15);
      } catch (e) { console.warn("Badge QR error:", e); }
    }

    // Status
    doc.setTextColor(50, 180, 80);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.text("APROBAT", w - 10.5, 29, { align: "center" });

    // Bottom line
    doc.setDrawColor(50, 180, 80);
    doc.setLineWidth(0.3);
    doc.line(3, 35, w - 3, 35);

    // Bottom info
    doc.setTextColor(120, 120, 120);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(4.5);
    doc.text("Acest ecuson permite accesul in stocul fizic si ridicarea achizitiilor.", 3, 39, { maxWidth: w - 6 });
    doc.text(`Garantie: ${badge.guarantee_amount} EUR`, 3, 44);
    doc.text(`Aprobat: ${badge.approved_at ? new Date(badge.approved_at).toLocaleDateString('ro-RO') : '-'}`, 3, 47);

    // Bottom branding
    doc.setTextColor(50, 180, 80);
    doc.setFontSize(5);
    doc.text("scutersh.store", w / 2, 52, { align: "center" });

    doc.save(`badge_${badge.client_id}_${badge.name.replace(/\s/g, '_')}.pdf`);
    return true;
  } catch (err) {
    console.error("Badge error:", err);
    return false;
  }
}

const STATUS_CONFIG = {
  upcoming: { label: "A venir", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  live: { label: "EN DIRECT", color: "bg-red-500/20 text-red-400 border-red-500/30" },
  ended: { label: "Terminee", color: "bg-gray-500/20 text-gray-400 border-gray-500/30" },
  cancelled: { label: "Annulee", color: "bg-orange-500/20 text-orange-400 border-orange-500/30" },
};

const PROPOSAL_STATUS = {
  pending: { label: "En attente", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  accepted: { label: "Acceptee", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  rejected: { label: "Rejetee", color: "bg-red-500/20 text-red-400 border-red-500/30" },
};

function AuctionRow({ auction, onEdit, onDelete, onRefresh, onFinalize }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[auction.status] || STATUS_CONFIG.upcoming;
  const [updating, setUpdating] = useState(false);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/auctions/${auction.auction_id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Enchere ${auction.auction_id} -> ${STATUS_CONFIG[newStatus]?.label}`);
      onRefresh();
    } catch (e) {
      toast.error("Erreur lors de la mise a jour");
    }
    setUpdating(false);
  };

  const handleSetWinner = async () => {
    if (!auction.bids?.length) {
      toast.error("Aucune offre pour cette enchere");
      return;
    }
    const lastBid = auction.bids[auction.bids.length - 1];
    const winner = `${lastBid.bidder_name} (${lastBid.amount} EUR)`;
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/auctions/${auction.auction_id}`, { 
        winner,
        status: "ended"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Gagnant: ${winner}`);
      onRefresh();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  return (
    <Card className="border-border/50" data-testid={`admin-auction-${auction.auction_id}`}>
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Gavel className="w-4 h-4 flex-shrink-0 text-primary" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-bold">{auction.auction_id}</span>
              <Badge className={`text-[10px] px-1.5 py-0 border ${cfg.color}`}>{cfg.label}</Badge>
            </div>
            <p className="text-sm truncate">{auction.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-bold text-primary">{auction.current_price} EUR</p>
            <p className="text-xs text-muted-foreground">{auction.bid_count || 0} offres</p>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>

      {expanded && (
        <CardContent className="pt-0 pb-4 space-y-4">
          {/* Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Prix depart</p>
              <p className="font-bold">{auction.starting_price} EUR</p>
            </div>
            <div>
              <p className="text-muted-foreground">Offre actuelle</p>
              <p className="font-bold text-primary">{auction.current_price} EUR</p>
            </div>
            <div>
              <p className="text-muted-foreground">Debut</p>
              <p className="font-mono text-xs">{new Date(auction.start_date).toLocaleString("fr-FR")}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Fin</p>
              <p className="font-mono text-xs">{new Date(auction.end_date).toLocaleString("fr-FR")}</p>
            </div>
          </div>

          {/* Bids */}
          {auction.bids?.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">Offres ({auction.bids.length})</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {[...auction.bids].reverse().map((bid, i) => (
                  <div key={i} className="flex justify-between items-center text-xs bg-secondary/30 rounded p-2">
                    <div className="flex items-center gap-2">
                      <span>{bid.bidder_name}</span>
                      <span className="text-muted-foreground">{bid.bidder_phone}</span>
                    </div>
                    <span className="font-bold text-primary">{bid.amount} EUR</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Winner */}
          {auction.winner && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-center gap-2">
              <Award className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-semibold">Gagnant: {auction.winner}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(STATUS_CONFIG).map(([key, val]) => (
              <Button
                key={key}
                size="sm"
                variant={auction.status === key ? "default" : "outline"}
                disabled={auction.status === key || updating}
                onClick={() => handleStatusChange(key)}
                className="text-xs h-7"
              >
                {val.label}
              </Button>
            ))}
            
            {auction.status === "live" && auction.bids?.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onFinalize(auction.auction_id)}
                className="text-xs h-7 border-orange-500/50 text-orange-400"
                data-testid={`finalize-${auction.auction_id}`}
              >
                <Award className="w-3 h-3 mr-1" />
                Finalizeaza
              </Button>
            )}

            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(auction)}
              className="text-xs h-7"
            >
              <Edit className="w-3 h-3 mr-1" />
              Modifier
            </Button>

            <Button
              size="sm"
              variant="destructive"
              onClick={() => onDelete(auction.auction_id)}
              className="text-xs h-7"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Supprimer
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function ProposalRow({ proposal, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = PROPOSAL_STATUS[proposal.status] || PROPOSAL_STATUS.pending;
  const [updating, setUpdating] = useState(false);
  const [notes, setNotes] = useState(proposal.admin_notes || "");
  const [auctionDate, setAuctionDate] = useState(proposal.auction_date || "");
  const [viewingDate, setViewingDate] = useState(proposal.viewing_date || "");

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/auction-proposals/${proposal.proposal_id}`, { 
        status: newStatus,
        admin_notes: notes,
        auction_date: auctionDate,
        viewing_date: viewingDate,
        notification_message: newStatus === "accepted" 
          ? `Produsul ${proposal.brand} ${proposal.model} a fost acceptat! ${auctionDate ? `Data licitatie: ${auctionDate}.` : ''} Va rugam sa il aduceti la sediul nostru.`
          : notes || "",
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Propunere ${proposal.proposal_id} -> ${PROPOSAL_STATUS[newStatus]?.label}`);
      onRefresh();
    } catch (e) {
      toast.error("Eroare");
    }
    setUpdating(false);
  };

  return (
    <Card className="border-border/50" data-testid={`admin-proposal-${proposal.proposal_id}`}>
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Package className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-bold">{proposal.proposal_id}</span>
              <Badge className={`text-[10px] px-1.5 py-0 border ${cfg.color}`}>{cfg.label}</Badge>
            </div>
            <p className="text-sm truncate">{proposal.brand} {proposal.model} ({proposal.year || "?"})</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <p className="text-sm">{proposal.proposer_name}</p>
            <p className="text-xs text-muted-foreground">{proposal.proposed_starting_price || "?"} EUR</p>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>

      {expanded && (
        <CardContent className="pt-0 pb-4 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Proposant</p>
              <p className="font-bold">{proposal.proposer_name}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Phone className="w-3 h-3" /> {proposal.proposer_phone}
              </p>
              {proposal.proposer_email && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {proposal.proposer_email}
                </p>
              )}
            </div>
            <div>
              <p className="text-muted-foreground">Produit</p>
              <p className="font-bold">{proposal.brand} {proposal.model}</p>
              <p className="text-xs">Annee: {proposal.year || "?"}</p>
              <p className="text-xs">Prix souhaite: {proposal.proposed_starting_price || "?"} EUR</p>
            </div>
          </div>

          {proposal.description && (
            <div>
              <p className="text-muted-foreground text-sm mb-1">Description</p>
              <p className="text-sm bg-secondary/30 rounded p-2">{proposal.description}</p>
            </div>
          )}

          <div>
            <Label className="text-sm">Note admin</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Note interne..."
              className="mt-1"
              rows={2}
            />
          </div>

          {/* Dates for scheduling */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Data licitatie</Label>
              <Input type="date" value={auctionDate} onChange={(e) => setAuctionDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data vizionare</Label>
              <Input type="date" value={viewingDate} onChange={(e) => setViewingDate(e.target.value)} />
            </div>
          </div>

          {/* Observations */}
          {proposal.observations && (
            <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-3">
              <p className="text-xs font-semibold text-orange-400 mb-1">Observatii vendeur:</p>
              <p className="text-sm text-muted-foreground">{proposal.observations}</p>
            </div>
          )}

          {/* Photos */}
          {(proposal.photos_product?.length > 0 || proposal.photos_damages?.length > 0 || proposal.photos_ruptures?.length > 0) && (
            <div className="space-y-2">
              {proposal.photos_product?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-blue-400 mb-1">Foto produs ({proposal.photos_product.length})</p>
                  <div className="flex gap-1 flex-wrap">
                    {proposal.photos_product.map((url, i) => <img key={i} src={url} alt="" className="w-14 h-14 object-cover rounded border border-border" />)}
                  </div>
                </div>
              )}
              {proposal.photos_damages?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-orange-400 mb-1">Foto daune ({proposal.photos_damages.length})</p>
                  <div className="flex gap-1 flex-wrap">
                    {proposal.photos_damages.map((url, i) => <img key={i} src={url} alt="" className="w-14 h-14 object-cover rounded border border-border" />)}
                  </div>
                </div>
              )}
              {proposal.photos_ruptures?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-yellow-400 mb-1">Foto rupturi ({proposal.photos_ruptures.length})</p>
                  <div className="flex gap-1 flex-wrap">
                    {proposal.photos_ruptures.map((url, i) => <img key={i} src={url} alt="" className="w-14 h-14 object-cover rounded border border-border" />)}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {Object.entries(PROPOSAL_STATUS).map(([key, val]) => (
              <Button
                key={key}
                size="sm"
                variant={proposal.status === key ? "default" : "outline"}
                disabled={proposal.status === key || updating}
                onClick={() => handleStatusChange(key)}
                className="text-xs h-7"
              >
                {val.label}
              </Button>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function CreateAuctionModal({ open, onClose, onSuccess, editAuction }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    starting_price: "",
    minimum_price: "",
    start_date: "",
    end_date: "",
    photos: [],
    scooter_id: "",
    chassis_number: "",
    document_status: "",
  });
  const [loading, setLoading] = useState(false);
  const [scooters, setScooters] = useState([]);
  const [loadingScooters, setLoadingScooters] = useState(false);
  const [mode, setMode] = useState("manual"); // manual or from_stock

  // Fetch scooters when modal opens
  useEffect(() => {
    if (open && mode === "from_stock") {
      setLoadingScooters(true);
      axios.get(`${API.replace('/api', '')}/api/public/store/scooters`)
        .then(res => {
          // Filter only available scooters (not sold, not in auction)
          const available = res.data.filter(s => 
            s.status !== "sold" && s.status !== "in_auction"
          );
          setScooters(available);
        })
        .catch(err => console.error(err))
        .finally(() => setLoadingScooters(false));
    }
  }, [open, mode]);

  useEffect(() => {
    if (editAuction) {
      setForm({
        title: editAuction.title || "",
        description: editAuction.description || "",
        starting_price: editAuction.starting_price?.toString() || "",
        minimum_price: editAuction.minimum_price?.toString() || "",
        start_date: editAuction.start_date?.slice(0, 16) || "",
        end_date: editAuction.end_date?.slice(0, 16) || "",
        photos: editAuction.photos || [],
        scooter_id: editAuction.scooter_id || "",
        chassis_number: editAuction.chassis_number || "",
        document_status: editAuction.document_status || "",
      });
      setMode("manual");
    } else {
      setForm({
        title: "",
        description: "",
        starting_price: "",
        minimum_price: "",
        start_date: "",
        end_date: "",
        photos: [],
        scooter_id: "",
        chassis_number: "",
        document_status: "",
      });
    }
  }, [editAuction, open]);

  const handleSelectScooter = (scooter) => {
    setForm({
      ...form,
      title: `${scooter.brand} ${scooter.model} ${scooter.year}`,
      description: scooter.notes || "",
      starting_price: scooter.selling_price?.toString() || "",
      minimum_price: scooter.selling_price?.toString() || "",
      scooter_id: scooter.scooter_id,
      chassis_number: scooter.chassis_number || "",
      document_status: scooter.document_status || "",
      photos: scooter.photos || [],
    });
    setMode("manual"); // Switch to manual to show the form
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.starting_price || !form.start_date || !form.end_date) {
      toast.error("Remplissez tous les champs obligatoires");
      return;
    }
    if (!form.minimum_price) {
      toast.error("Le prix minimum de vente est obligatoire");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        ...form,
        starting_price: parseFloat(form.starting_price),
        minimum_price: parseFloat(form.minimum_price),
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString(),
      };

      if (editAuction) {
        await axios.put(`${API}/auctions/${editAuction.auction_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Enchere mise a jour!");
      } else if (form.scooter_id) {
        // Create from existing scooter
        await axios.post(`${API}/auctions/from-scooter/${form.scooter_id}`, {
          description: form.description,
          starting_price: parseFloat(form.starting_price),
          minimum_price: parseFloat(form.minimum_price),
          start_date: new Date(form.start_date).toISOString(),
          end_date: new Date(form.end_date).toISOString(),
          bid_increment: 20,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Enchere creee depuis le stock!");
      } else {
        await axios.post(`${API}/auctions`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Enchere creee!");
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gavel className="w-5 h-5 text-primary" />
            {editAuction ? "Modifier l'enchere" : "Nouvelle enchere"}
          </DialogTitle>
        </DialogHeader>

        {!editAuction && (
          <div className="flex gap-2 mb-4">
            <Button
              type="button"
              variant={mode === "manual" ? "default" : "outline"}
              onClick={() => setMode("manual")}
              className="flex-1"
            >
              <Plus className="w-4 h-4 mr-2" />
              Manuel
            </Button>
            <Button
              type="button"
              variant={mode === "from_stock" ? "default" : "outline"}
              onClick={() => setMode("from_stock")}
              className="flex-1"
            >
              <Package className="w-4 h-4 mr-2" />
              Depuis le Stock
            </Button>
          </div>
        )}

        {mode === "from_stock" && !editAuction ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Selectionnez un scooter de votre stock:
            </p>
            {loadingScooters ? (
              <div className="text-center py-8">Chargement...</div>
            ) : scooters.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Aucun scooter disponible
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {scooters.map(scooter => (
                  <Card
                    key={scooter.scooter_id}
                    className="cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => handleSelectScooter(scooter)}
                  >
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                        {scooter.photos?.[0] ? (
                          <img src={scooter.photos[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Package className="w-full h-full p-2 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">
                          {scooter.brand} {scooter.model}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {scooter.year} • {scooter.engine_cc}cc • {scooter.mileage?.toLocaleString()} km
                        </p>
                        <p className="text-sm font-bold text-primary">
                          {scooter.selling_price} EUR
                        </p>
                      </div>
                      <Badge variant="outline" className="flex-shrink-0">
                        {scooter.document_status || "—"}
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {form.scooter_id && (
              <div className="bg-primary/10 rounded-lg p-3 text-sm">
                <p className="font-semibold flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Scooter selectionne: {form.scooter_id}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setForm({ ...form, scooter_id: "" });
                    setMode("from_stock");
                  }}
                  className="mt-1 h-6 text-xs"
                >
                  Changer de scooter
                </Button>
              </div>
            )}

            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ex: Piaggio Liberty 50cc 2020"
                data-testid="auction-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Description detaillee..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prix de depart (EUR) *</Label>
                <Input
                  type="number"
                  value={form.starting_price}
                  onChange={(e) => setForm({ ...form, starting_price: e.target.value })}
                  placeholder="500"
                  data-testid="auction-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Prix minimum vente (EUR) *</Label>
                <Input
                  type="number"
                  value={form.minimum_price}
                  onChange={(e) => setForm({ ...form, minimum_price: e.target.value })}
                  placeholder="800"
                  data-testid="auction-min-price-input"
                />
                <p className="text-xs text-muted-foreground">Si non atteint: +1 min, puis annulation</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>N° Chassis</Label>
              <Input
                value={form.chassis_number}
                onChange={(e) => setForm({ ...form, chassis_number: e.target.value })}
                placeholder="ZAPM67100..."
              />
            </div>

            <div className="space-y-2">
              <Label>Etat des documents</Label>
              <Select value={form.document_status} onValueChange={(v) => setForm({ ...form, document_status: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selectionnez..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="acte_romania">Acte Romania</SelectItem>
                  <SelectItem value="acte_strainatate">Acte strainatate</SelectItem>
                  <SelectItem value="fara_acte">Doar factura</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de debut *</Label>
                <Input
                  type="datetime-local"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="auction-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin *</Label>
                <Input
                  type="datetime-local"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="auction-end-input"
                />
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={loading} data-testid="submit-auction-btn">
              {loading ? "Envoi..." : editAuction ? "Mettre a jour" : "Creer l'enchere"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminAuctionsPage() {
  const { user } = useAuth();
  const [auctions, setAuctions] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [cancelledAuctions, setCancelledAuctions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [sessionStats, setSessionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("auctions"); // auctions, proposals, clients, sessions, history
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editAuction, setEditAuction] = useState(null);
  const [sessionModalOpen, setSessionModalOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [auctionsRes, proposalsRes, clientsRes, sessionsRes, cancelledRes, reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API}/auctions`),
        axios.get(`${API}/auction-proposals`, { headers }),
        axios.get(`${API}/auction-clients`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/live-sessions`).catch(() => ({ data: [] })),
        axios.get(`${API}/auctions/history/cancelled`).catch(() => ({ data: [] })),
        axios.get(`${API}/reviews`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/session-stats`, { headers }).catch(() => ({ data: null })),
      ]);
      
      setAuctions(auctionsRes.data);
      setProposals(proposalsRes.data);
      setClients(clientsRes.data);
      setSessions(sessionsRes.data);
      setCancelledAuctions(cancelledRes.data);
      setReviews(reviewsRes.data);
      setSessionStats(statsRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Erreur de chargement");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (auctionId) => {
    if (!window.confirm("Supprimer cette enchere?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/auctions/${auctionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Enchere supprimee");
      fetchData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleEdit = (auction) => {
    setEditAuction(auction);
    setCreateModalOpen(true);
  };

  const handleFinalizeAuction = async (auctionId) => {
    if (!confirm("Finalizati aceasta licitatie? Castigatorul va fi notificat.")) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/auctions/${auctionId}/finalize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare");
    }
  };

  const handleApproveClient = async (clientId, guaranteeAmount) => {
    const amount = prompt("Montant de la garantie (EUR):", "100");
    if (!amount) return;
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/auction-clients/${clientId}/approve`, {
        guarantee_amount: parseFloat(amount),
        admin_notes: ""
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Client approuve!");
      fetchData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleRejectClient = async (clientId) => {
    if (!window.confirm("Rejeter ce client?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/auction-clients/${clientId}/reject?notes=Rejete`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Client rejete");
      fetchData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleCreateSession = async () => {
    const name = prompt("Nom de la session:", `Session ${new Date().toLocaleDateString()}`);
    if (!name) return;
    const selectedAuctions = auctions.filter(a => a.status === "upcoming" || a.status === "live");
    if (selectedAuctions.length === 0) {
      toast.error("Aucune enchere disponible");
      return;
    }
    const startTime = prompt("Date/heure de debut (YYYY-MM-DDTHH:mm):", new Date().toISOString().slice(0, 16));
    if (!startTime) return;
    const duration = prompt("Duree par scooter (minutes):", "5");
    if (!duration) return;

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/live-sessions`, {
        name,
        auction_ids: selectedAuctions.map(a => a.auction_id),
        slot_duration_minutes: parseInt(duration),
        start_time: new Date(startTime).toISOString()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Session creee!");
      fetchData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm("Supprimer cette session?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/live-sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Session supprimee");
      fetchData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const stats = {
    total: auctions.length,
    live: auctions.filter(a => a.status === "live").length,
    upcoming: auctions.filter(a => a.status === "upcoming").length,
    pendingProposals: proposals.filter(p => p.status === "pending").length,
    pendingClients: clients.filter(c => c.status === "pending").length,
    approvedClients: clients.filter(c => c.status === "approved").length,
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6" data-testid="admin-auctions-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
              <Gavel className="w-6 h-6 text-primary" />
              Gestion des Encheres
            </h1>
            <p className="text-sm text-muted-foreground">Gerez vos encheres et propositions</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/auctions" target="_blank">
              <Button variant="outline" size="sm">
                <Eye className="w-4 h-4 mr-2" />
                Voir page publique
              </Button>
            </Link>
            <Button onClick={() => { setEditAuction(null); setCreateModalOpen(true); }} data-testid="create-auction-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle enchere
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Gavel className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total encheres</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <Timer className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-400">{stats.live}</p>
                <p className="text-xs text-muted-foreground">En direct</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Calendar className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-400">{stats.upcoming}</p>
                <p className="text-xs text-muted-foreground">A venir</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <Package className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-400">{stats.pendingProposals}</p>
                <p className="text-xs text-muted-foreground">Propositions</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border/50 overflow-x-auto">
          <button
            onClick={() => setActiveTab("auctions")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "auctions" 
                ? "border-primary text-primary" 
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Encheres ({auctions.length})
          </button>
          <button
            onClick={() => setActiveTab("clients")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "clients" 
                ? "border-primary text-primary" 
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Clients ({clients.length})
            {stats.pendingClients > 0 && (
              <Badge className="ml-2 bg-red-500/20 text-red-400">{stats.pendingClients}</Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab("sessions")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "sessions" 
                ? "border-primary text-primary" 
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Sessions Live ({sessions.length})
          </button>
          <button
            onClick={() => setActiveTab("proposals")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "proposals" 
                ? "border-primary text-primary" 
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Propositions ({proposals.length})
            {stats.pendingProposals > 0 && (
              <Badge className="ml-2 bg-yellow-500/20 text-yellow-400">{stats.pendingProposals}</Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "history" 
                ? "border-primary text-primary" 
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Historique ({cancelledAuctions.length})
          </button>
          <button
            onClick={() => setActiveTab("reviews")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "reviews" 
                ? "border-primary text-primary" 
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Avis ({reviews.length})
          </button>
          <button
            onClick={() => setActiveTab("results")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "results" 
                ? "border-primary text-primary" 
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Rezultate
          </button>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="h-20" />
              </Card>
            ))}
          </div>
        ) : activeTab === "auctions" ? (
          <div className="space-y-3">
            {auctions.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Gavel className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Aucune enchere</h3>
                  <p className="text-sm text-muted-foreground mb-4">Creez votre premiere enchere</p>
                  <Button onClick={() => setCreateModalOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Creer une enchere
                  </Button>
                </CardContent>
              </Card>
            ) : (
              auctions.map(auction => (
                <AuctionRow
                  key={auction.auction_id}
                  auction={auction}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onRefresh={fetchData}
                  onFinalize={handleFinalizeAuction}
                />
              ))
            )}
          </div>
        ) : activeTab === "clients" ? (
          /* CLIENTS TAB */
          <div className="space-y-3">
            <div className="flex gap-2 mb-4">
              <Badge className="bg-yellow-500/20 text-yellow-400">En attente: {stats.pendingClients}</Badge>
              <Badge className="bg-emerald-500/20 text-emerald-400">Approuves: {stats.approvedClients}</Badge>
            </div>
            {clients.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Aucun client inscrit</h3>
                  <p className="text-sm text-muted-foreground">Les clients s'inscrivent depuis la page encheres live</p>
                </CardContent>
              </Card>
            ) : (
              clients.map(client => (
                <Card key={client.client_id} className="border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          client.status === 'approved' ? 'bg-emerald-500' :
                          client.status === 'pending' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`} />
                        <div>
                          <p className="font-semibold">{client.name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Phone className="w-3 h-3" /> {client.phone}
                            {client.email && <><Mail className="w-3 h-3 ml-2" /> {client.email}</>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {client.status === 'approved' && (
                          <>
                            <Badge className="bg-emerald-500/20 text-emerald-400">
                              <Shield className="w-3 h-3 mr-1" /> Garantie: {client.guarantee_amount} EUR
                            </Badge>
                            <Button size="sm" variant="outline" onClick={async (e) => {
                              e.stopPropagation();
                              toast.loading("Génération du badge...");
                              const ok = await generateBadgePDF(client);
                              toast.dismiss();
                              if (ok) toast.success("Badge PDF téléchargé !");
                              else toast.error("Erreur génération badge");
                            }} data-testid={`badge-btn-${client.client_id}`}>
                              <Printer className="w-4 h-4 mr-1" /> Badge
                            </Button>
                          </>
                        )}
                        {client.photo_url && (
                          <img src={client.photo_url} alt="" className="w-8 h-8 rounded-full object-cover border border-border" />
                        )}
                        {client.status === 'pending' && (
                          <>
                            <Button size="sm" onClick={() => handleApproveClient(client.client_id)} className="bg-emerald-600 hover:bg-emerald-700">
                              <CheckCircle className="w-4 h-4 mr-1" /> Approuver
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleRejectClient(client.client_id)}>
                              <XCircle className="w-4 h-4 mr-1" /> Rejeter
                            </Button>
                          </>
                        )}
                        <Badge className={
                          client.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                          client.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }>
                          {client.status}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Inscrit le {new Date(client.created_at).toLocaleString('fr-FR')}
                      {client.approved_at && ` • Approuve le ${new Date(client.approved_at).toLocaleString('fr-FR')}`}
                    </p>
                    {/* Password display for admin */}
                    {client.password_plain && (
                      <div className="mt-2 flex items-center gap-2">
                        <Badge className="bg-blue-500/20 text-blue-400 font-mono text-xs">
                          MDP: {client.password_plain}
                        </Badge>
                        <Button size="sm" variant="ghost" className="h-6 px-2" onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(client.password_plain);
                          toast.success("Mot de passe copié !");
                        }}>
                          Copier
                        </Button>
                      </div>
                    )}
                    {/* Company info */}
                    {client.client_type === "company" && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Badge variant="outline" className="text-xs border-orange-500/30 text-orange-400">
                          Entreprise
                        </Badge>
                        {client.company_name && <span className="text-xs text-muted-foreground">{client.company_name}</span>}
                        {client.cui && <span className="text-xs text-muted-foreground">CUI: {client.cui}</span>}
                        {client.reg_commerce && <span className="text-xs text-muted-foreground">RC: {client.reg_commerce}</span>}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        ) : activeTab === "sessions" ? (
          /* SESSIONS TAB */
          <div className="space-y-3">
            <Button onClick={handleCreateSession} className="mb-4">
              <Play className="w-4 h-4 mr-2" /> Creer Session Live
            </Button>
            <div className="bg-secondary/30 rounded-lg p-4 mb-4">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Eye className="w-4 h-4 text-primary" /> Page Live
              </h4>
              <p className="text-sm text-muted-foreground mb-2">Les clients regardent les encheres sur:</p>
              <code className="bg-black/30 px-2 py-1 rounded text-primary text-sm">
                {window.location.origin}/live
              </code>
            </div>
            {sessions.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Play className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Aucune session</h3>
                  <p className="text-sm text-muted-foreground">Creez une session pour demarrer les encheres live</p>
                </CardContent>
              </Card>
            ) : (
              sessions.map(session => (
                <Card key={session.session_id} className="border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{session.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {session.auction_ids?.length || 0} encheres • {session.slot_duration_minutes} min/scooter
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Debut: {new Date(session.start_time).toLocaleString('fr-FR')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          session.status === 'live' ? 'bg-red-500/20 text-red-400 animate-pulse' :
                          session.status === 'ended' ? 'bg-gray-500/20 text-gray-400' :
                          'bg-blue-500/20 text-blue-400'
                        }>
                          {session.status === 'live' ? 'EN DIRECT' : session.status}
                        </Badge>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteSession(session.session_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        ) : activeTab === "proposals" ? (
          /* PROPOSALS TAB */
          <div className="space-y-3">
            {proposals.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Aucune proposition</h3>
                  <p className="text-sm text-muted-foreground">Les clients peuvent proposer leurs produits depuis la page encheres</p>
                </CardContent>
              </Card>
            ) : (
              proposals.map(proposal => (
                <ProposalRow
                  key={proposal.proposal_id}
                  proposal={proposal}
                  onRefresh={fetchData}
                />
              ))
            )}
          </div>
        ) : activeTab === "history" ? (
          /* CANCELLED AUCTIONS HISTORY TAB */
          <div className="space-y-3" data-testid="history-tab">
            {cancelledAuctions.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Aucune enchère annulée</h3>
                  <p className="text-sm text-muted-foreground">Les enchères qui n'atteignent pas le prix minimum apparaîtront ici</p>
                </CardContent>
              </Card>
            ) : (
              cancelledAuctions.map(auction => (
                <Card key={auction.auction_id} className="border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{auction.title}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                          <span>Départ: {auction.starting_price} EUR</span>
                          <span>Final: {auction.current_price || auction.starting_price} EUR</span>
                          {auction.minimum_price > 0 && <span>Min requis: {auction.minimum_price} EUR</span>}
                          <span>{(auction.bids || []).length} enchères</span>
                        </div>
                      </div>
                      <Badge className={
                        auction.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                        auction.status === 'ended' ? 'bg-gray-500/20 text-gray-400' :
                        'bg-orange-500/20 text-orange-400'
                      }>
                        {auction.status === 'cancelled' ? 'Annulée' : auction.status === 'ended' ? 'Terminée' : auction.status}
                      </Badge>
                    </div>
                    {auction.bids && auction.bids.length > 0 && (
                      <div className="mt-3 bg-secondary/30 rounded-lg p-3">
                        <p className="text-xs font-semibold mb-2">Dernières enchères:</p>
                        {auction.bids.slice(-3).reverse().map((bid, i) => (
                          <div key={i} className="flex justify-between text-xs py-1">
                            <span className="text-muted-foreground">{bid.client_name}</span>
                            <span className="font-bold">{bid.amount} EUR</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        ) : activeTab === "reviews" ? (
          /* REVIEWS TAB */
          <div className="space-y-3" data-testid="reviews-tab">
            {reviews.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Award className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Aucun avis</h3>
                  <p className="text-sm text-muted-foreground">Les avis des clients apparaîtront ici après les enchères</p>
                </CardContent>
              </Card>
            ) : (
              reviews.map(review => (
                <Card key={review.review_id} className="border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{review.client_name}</p>
                        <div className="flex items-center gap-1 mt-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <span key={star} className={`text-sm ${star <= review.rating ? 'text-yellow-400' : 'text-gray-600'}`}>
                              &#9733;
                            </span>
                          ))}
                          <span className="text-xs text-muted-foreground ml-2">{review.rating}/5</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="text-xs">{review.auction_id}</Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(review.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    {review.comment && (
                      <p className="text-sm text-muted-foreground mt-2 bg-secondary/30 rounded-lg p-3">
                        "{review.comment}"
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        ) : (
          /* RESULTS TAB */
          <div className="space-y-4" data-testid="results-tab">
            {/* Stats overview */}
            {sessionStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-primary">{sessionStats.connected_clients}</p>
                    <p className="text-xs text-muted-foreground">Clienti conectati acum</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{sessionStats.total_ended_auctions}</p>
                    <p className="text-xs text-muted-foreground">Licitatii finalizate</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-400">{sessionStats.auction_results?.filter(a => a.winner).length || 0}</p>
                    <p className="text-xs text-muted-foreground">Cu castigator</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-primary">
                      {sessionStats.auction_results?.reduce((sum, a) => sum + (a.winner?.final_price || 0), 0).toLocaleString()} EUR
                    </p>
                    <p className="text-xs text-muted-foreground">Total vanzari</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Auction results list */}
            {sessionStats?.auction_results?.map(result => (
              <Card key={result.auction_id} className={`border-border/50 ${result.winner ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-red-500'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-semibold">{result.title}</h3>
                      <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                        <span>Start: {result.starting_price} EUR</span>
                        <span>Final: {result.final_price} EUR</span>
                        <span>{result.total_bids} oferte</span>
                        <span>{result.unique_bidders} participanti</span>
                        {result.views > 0 && <span>{result.views} vizualizari</span>}
                      </div>
                    </div>
                    <Badge className={result.winner ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                      {result.winner ? 'Finalizat' : result.status === 'cancelled' ? 'Anulat' : 'Terminat'}
                    </Badge>
                  </div>

                  {result.winner && (
                    <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 mt-2">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">Castigator</p>
                          <p className="font-semibold text-emerald-400">{result.winner.winner_name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Pret final</p>
                          <p className="font-bold">{result.winner.final_price} EUR</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Comision</p>
                          <p className="font-semibold">{result.winner.commission} EUR</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Plata</p>
                          <Badge variant="outline" className="text-xs">
                            {result.winner.payment_status === 'paid' ? 'Platit' : 'In asteptare'}
                          </Badge>
                        </div>
                      </div>
                      {result.winner.payment_method && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          Metoda plata: {result.winner.payment_method === 'transfer' ? 'Transfer bancar' : 'Cash'} | 
                          Livrare: {result.winner.delivery_method === 'pickup' ? 'Ridicare la sediu' : result.winner.delivery_method === 'delivery' ? 'Livrare' : 'Nespecificat'}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {(!sessionStats?.auction_results || sessionStats.auction_results.length === 0) && (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Award className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Niciun rezultat</h3>
                  <p className="text-sm text-muted-foreground">Rezultatele licitatiilor finalizate vor aparea aici</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Create/Edit Modal */}
        <CreateAuctionModal
          open={createModalOpen}
          onClose={() => { setCreateModalOpen(false); setEditAuction(null); }}
          onSuccess={fetchData}
          editAuction={editAuction}
        />
      </div>
    </Layout>
  );
}
